export default function LiensSection() {
  const lienTypes = [
    {
      icon: 'ri-government-line',
      title: 'Tax Liens',
      description: 'Federal, state, or county tax liens can attach to your property when taxes go unpaid. These take priority over almost all other debts and must be satisfied before the property can be sold with clear title.',
      solution: 'We pay off tax liens directly at closing from the sale proceeds, so you never have to come out of pocket.'
    },
    {
      icon: 'ri-hammer-line',
      title: 'Mechanic\'s Liens',
      description: 'Contractors, subcontractors, or suppliers who weren\'t paid for work on the property can file mechanic\'s liens. These can be filed months after the work was completed, catching heirs by surprise.',
      solution: 'We negotiate with lien holders and settle these claims as part of the purchase, removing your liability.'
    },
    {
      icon: 'ri-parent-line',
      title: 'Child Support Liens',
      description: 'If the previous owner owed back child support, the state can place a lien on the property. These liens transfer with the property and must be cleared before sale.',
      solution: 'We work with the state to resolve child support liens at closing, protecting you from enforcement actions.'
    },
    {
      icon: 'ri-hospital-line',
      title: 'Medicaid Estate Recovery',
      description: 'When someone receives Medicaid benefits and passes away, the state may file a claim against their estate to recover costs. This creates a lien on inherited property that must be satisfied.',
      solution: 'We handle Medicaid estate recovery claims directly with the state, negotiating settlements when possible.'
    },
    {
      icon: 'ri-scales-3-line',
      title: 'Judgment Liens',
      description: 'Court judgments from lawsuits, unpaid debts, or credit card judgments can attach to real property. These liens follow the property, not the person, making them the heir\'s problem.',
      solution: 'We pay off judgment liens at closing, clearing the title so you can sell without legal complications.'
    },
    {
      icon: 'ri-home-heart-line',
      title: 'HOA Liens',
      description: 'Homeowners association dues and special assessments can result in liens with high interest rates and attorney fees. HOAs can even foreclose on properties for unpaid dues.',
      solution: 'We settle all HOA debts and bring accounts current as part of our purchase, eliminating your exposure.'
    }
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Resolving <strong>Undisclosed or Unknown Liens</strong>
          </h2>
          <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
            When you inherit a house in El Paso, you often inherit hidden debt too. Liens can lurk in public records for years, only surfacing when you try to sell. These undisclosed liens can derail traditional sales, but <strong>we buy houses with liens</strong> and handle all the complications for you.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/0b8ef7d354c3ff522122a9747faade80.jpg"
              alt="Property lien documents and tax notices"
              className="w-full h-auto rounded-lg shadow-xl"
            />
          </div>
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">The Hidden Debt Problem</h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Most heirs discover liens only after ordering a title search. By then, they've already invested time and money into preparing the property for sale. The shock of discovering <strong>$15,000 in back taxes</strong> or <strong>$8,000 in mechanic's liens</strong> can be devastating.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Traditional buyers will walk away immediately. Banks won't approve mortgages on properties with liens. You're left with three bad options: pay the liens yourself (often impossible), wait months for legal resolution, or abandon the sale entirely.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              <strong>As cash home buyers in El Paso, we offer a fourth option:</strong> We buy your house as-is, liens and all. We pay off the liens at closing from the purchase proceeds, so you never have to come out of pocket. You walk away with cash in hand, and we handle all the legal complications.
            </p>
            <div className="bg-emerald-500 text-white p-6 rounded-lg">
              <p className="text-lg font-semibold mb-2">Average Lien Amount We've Paid Off:</p>
              <p className="text-4xl font-bold">$23,400</p>
              <p className="text-sm mt-2">Across 200+ transactions in El Paso</p>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {lienTypes.map((lien, index) => (
            <div key={index} className="bg-white rounded-lg shadow-lg p-8 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mb-6">
                <i className={`${lien.icon} text-3xl text-white`}></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{lien.title}</h3>
              <p className="text-gray-700 mb-4 leading-relaxed">{lien.description}</p>
              <div className="border-t border-gray-200 pt-4">
                <p className="text-sm font-semibold text-emerald-600 mb-2">Our Solution:</p>
                <p className="text-gray-700 text-sm leading-relaxed">{lien.solution}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 bg-white rounded-lg shadow-xl p-8 md:p-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-6 text-center">
            Why Selling to Us Beats Paying Liens Yourself
          </h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h4 className="text-xl font-bold text-red-600 mb-4">Traditional Approach (Painful)</h4>
              <div className="flex items-start gap-3">
                <i className="ri-close-circle-line text-2xl text-red-600 flex-shrink-0"></i>
                <p className="text-gray-700">Pay liens out of pocket before you can sell</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-close-circle-line text-2xl text-red-600 flex-shrink-0"></i>
                <p className="text-gray-700">Wait 3-6 months for lien releases to be recorded</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-close-circle-line text-2xl text-red-600 flex-shrink-0"></i>
                <p className="text-gray-700">Hire attorneys to negotiate with lien holders</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-close-circle-line text-2xl text-red-600 flex-shrink-0"></i>
                <p className="text-gray-700">Risk paying more than necessary without expertise</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-close-circle-line text-2xl text-red-600 flex-shrink-0"></i>
                <p className="text-gray-700">Still need to find a buyer after clearing liens</p>
              </div>
            </div>
            <div className="space-y-4">
              <h4 className="text-xl font-bold text-emerald-600 mb-4">Selling to T-Rex (Easy)</h4>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <p className="text-gray-700">We pay off all liens at closing from proceeds</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <p className="text-gray-700">Close in 7-14 days, not months</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <p className="text-gray-700">Our team handles all negotiations and paperwork</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <p className="text-gray-700">We have relationships with lien holders for better settlements</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <p className="text-gray-700">You get cash in hand without any out-of-pocket costs</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}